import React from 'react';
import { connect } from 'react-redux';
import { Button, Modal, Switch } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';
import Icon from '@ant-design/icons/lib/components/Icon';
import { ReactComponent as SxCrossSVG } from '../../../assets/sightx-icons/svg/x-close.svg';
import { ISectionsAccessPermissions } from '../../../slices/users/user';
import { IUpdateUserPermissionsData, updateSectionAccessPermissions } from '../../../slices/users/updateSectionAccessPermissions.thunks';
import { RootState } from '../../../slices/rootReducer';
import { selectors as userFormSelectors } from '../../../slices/users/form';
import { SxTooltip } from '../../common/utils/wrapComponents';

import styles from './sectionAccessPermissionsModal.module.scss';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';

interface IState {
  errorMessage: string | null;
}
interface IComponentProps {
  visible: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
  permissions: ISectionsAccessPermissions;
}

interface IMapStateToProps {
  isFormLoading: boolean;
  formErrors: string | null;
}

interface IDispatchProps {
  updateSectionAccessPermissions: (data: IUpdateUserPermissionsData) => void;
}

type Props = IComponentProps & IDispatchProps & IMapStateToProps;

class SectionAccessPermissionsModalBase extends React.Component<Props, IState> {
  private formRef: React.RefObject<FormInstance<ISectionsAccessPermissions>>;
  constructor(props: Props) {
    super(props);
    this.formRef = React.createRef<FormInstance<ISectionsAccessPermissions>>();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.state = {
      errorMessage: null
    };
  }

  componentDidUpdate(prevProps: Props): void {
    const { isFormLoading: wasFormLoading } = prevProps;
    const { formErrors, isFormLoading, onClose } = this.props;
    if (wasFormLoading && !isFormLoading && !formErrors) {
      onClose();
    }
  }

  private handleSubmit() {
    const { updateSectionAccessPermissions, userId } = this.props;
    const permissions = this.formRef.current?.getFieldsValue();
    if (permissions) {
      updateSectionAccessPermissions({
        userId,
        permissions
      });
    }
  }

  private validateForm() {
    const { analysis, build } = this.formRef.current?.getFieldsValue() ?? {};
    if (!analysis && !build) {
      this.setState({
        errorMessage: 'Distribute has to be activated with at least another section.'
      });
      return;
    }
    this.setState({
      errorMessage: null
    });
  }

  private renderPermissionToggle(name: string, label: string) {
    return <div className={styles.permission}>
      <Form.Item name={name} valuePropName="checked">
        <Switch className={styles.selector} size="small" onChange={this.validateForm} />
      </Form.Item>
      <span className={styles.label}>{label}</span>
    </div>;
  }

  private renderActions() {
    const { onClose, isFormLoading } = this.props;
    const { errorMessage } = this.state;
    return <div className={styles.actions}>
      <Button
        type="ghost"
        onClick={onClose}
      >
        Cancel
      </Button>
      <SxTooltip
        Component={<Button
          type="primary"
          htmlType="submit"
          loading={isFormLoading}
          disabled={!!errorMessage}
          className={styles.tooltipContainer}
        >
          Save
        </Button>}
        tooltipText={errorMessage}
        hideTooltip={!errorMessage}
      ></SxTooltip>

    </div>;
  }

  private renderForm() {
    const { permissions } = this.props;
    return <Form
      ref={this.formRef}
      initialValues={permissions}
      onFinish={this.handleSubmit}
      requiredMark={false}
      layout="vertical"
    >
      {this.renderPermissionToggle('build', 'Build')}
      {this.renderPermissionToggle('distribute', 'Distribute')}
      {this.renderPermissionToggle('analysis', 'Analysis')}
      {isFeatureEnabled(Features.Branding) && this.renderPermissionToggle('branding', 'Branding')}
      {this.renderActions()}
    </Form >;
  }

  private renderTitle() {
    const { userEmail } = this.props;
    return <div className={styles.title}>
      Edit access for
      <br />
      <span className={styles.email}>{userEmail}</span>
    </div>;
  }

  render(): React.ReactNode {
    const { visible, onClose } = this.props;
    return <Modal
      visible={visible}
      title={this.renderTitle()}
      footer={null}
      onCancel={onClose}
      closeIcon={<Icon component={SxCrossSVG} />}
      centered
      maskClosable={false}
      width={450}
      className={styles.modal}
    >
      <div className={styles.description}>Sections this user has access to:</div>
      {this.renderForm()}
    </Modal>;
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => ({
  formErrors: userFormSelectors.getUserFormError(state),
  isFormLoading: userFormSelectors.getUserFormLoading(state)
});

const dispatchProps: IDispatchProps = {
  updateSectionAccessPermissions
};

export const SectionAccessPermissionsModal = connect(mapStateToProps, dispatchProps)(SectionAccessPermissionsModalBase);
